/**
 * WorkflowGraph — foundation layer
 *
 * Extends maxGraph's Graph class with:
 * - Registered custom shapes (materialHexagon, operationPill)
 * - Plugin suite (selection, rubberband, panning, tooltip, popup, connection, keyboard, editor)
 * - Default vertex/edge styles
 * - Orthogonal edge routing  
 * - Interaction constraints (read-only cells — no move/resize/inline-edit)
 * - Typed event API so React layers never import maxGraph types
 * - Clean teardown via destroy()
 *
 * Framework-agnostic: no React, no MUI, no state management imports.
 */

import {
  Graph,
  Cell,
  Geometry,
  HierarchicalLayout,
  LayoutManager,
  InternalEvent,
  PanningHandler,
  UndoManager,
  EventObject,
  ImageBox,
} from '@maxgraph/core';
import { getPluginList } from './config/plugins';
import {
  DEFAULT_VERTEX_STYLE,
  DEFAULT_EDGE_STYLE,
  GRID_BACKGROUND_URL,
} from './config/defaults';
import { registerDefaultShapes } from './shapes';
import { GROUP_STYLE, OPERATION_EDGE_STYLE, REMAINDER_EDGE_STYLE } from './styles';
import { TypedEventBus } from './events';
import type {
  WorkflowGraphConfig,
  WorkflowGraphEvent,
  WorkflowGraphEventListener,
} from './types';

// Domain primitives
import type {
  MaterialNodeSpec,
  OperationNodeSpec,
  GroupSpec,
  GraphSnapshot,
} from './domain/types';
import { addMaterialNode, updateMaterialNode, removeMaterialNode } from './domain/materialNode';
import { addOperationNode, updateOperationNode, removeOperationNode } from './domain/operationNode';
import { addGroup, updateGroup, removeGroup, addStepsToGroup, removeStepsFromGroup, foldGroup } from './domain/group';
import { hydrateFromSnapshot, toSnapshot, clearGraph } from './domain/snapshot';

export class WorkflowGraph extends Graph {
  readonly undoManager: UndoManager;

  private disposed = false;
  private bus = new TypedEventBus();
  private _pendingGroupSpec: { id: string; label: string } | null = null;

  // Ops-as-edges: two store edges (mat→op, op→mat) collapse into one visual edge (mat→mat).
  // _pendingEdgeHalves maps operationId → array of pending edge halves (can have multiple inputs/outputs for COMPLEX).
  // Each pending half stores the storeEdgeId, materialId, side, and displayName.
  // When a matching pair arrives (source+target for same operation), they create one visual edge.
  // _storeEdgeToOpId maps each store edge ID to its operationId for removal.
  private _pendingEdgeHalves = new Map<string, Array<{ materialId: string; side: 'source' | 'target'; storeEdgeId: string; displayName?: string }>>();
  private _storeEdgeToOpId = new Map<string, string>();

  // Held as references so they can be removed on destroy
  private _modelChangeListener?: (sender: unknown, evt: EventObject) => void;
  private _selectionChangeListener?: (sender: unknown, evt: EventObject) => void;
  private _undoListener?: (sender: unknown, evt: EventObject) => void;
  private _cellsAddedListener?: (sender: unknown, evt: EventObject) => void;
  private _cellsRemovedListener?: (sender: unknown, evt: EventObject) => void;
  private _undoManagerUndoListener?: (sender: unknown, evt: EventObject) => void;
  private _undoManagerRedoListener?: (sender: unknown, evt: EventObject) => void;
  private _foldCellsListener?: (sender: unknown, evt: EventObject) => void;
  private _layoutManager?: LayoutManager;

  constructor(config: WorkflowGraphConfig) {
    super(config.container, undefined, getPluginList());

    registerDefaultShapes();
    this.applyDefaults();
    this.setupLayoutManager();
    this.configureInteraction(config);
    this.configureRouting();

    this.undoManager = new UndoManager();
    this.wireUndoManager();
    this.wireEventEmitters();

    // Suppress the browser's native context menu on the graph container.
    // maxGraph's PopupMenuHandler consumes its internal event but does not
    // call preventDefault on the native DOM event.
    InternalEvent.disableContextMenu(config.container);

    // Group cells (swimlanes) are foldable — all others are not.
    this.isCellFoldable = (cell: Cell) => {
      const val = cell.getValue();
      return val != null && typeof val === 'object' && val.kind === 'group';
    };

    // Override createGroupCell so groupCells() creates our custom group cell
    // with the correct id, label, value, and style. _pendingGroupSpec is set
    // by addGroup() before calling groupCells() (synchronous) and cleared after.
    const originalCreateGroupCell = this.createGroupCell.bind(this);
    this.createGroupCell = (cells: Cell[]): Cell => {
      if (this._pendingGroupSpec) {
        const { id, label } = this._pendingGroupSpec;
        const cell = new Cell(
          { kind: 'group', label, id },
          new Geometry(),
        );
        cell.setId(id);
        cell.setVertex(true);
        cell.setConnectable(false);
        cell.setStyle(GROUP_STYLE);
        return cell;
      }
      return originalCreateGroupCell(cells);
    };
  }

  /**
   * Subscribe to typed graph events.
   * @returns unsubscribe function
   */
  on(listener: WorkflowGraphEventListener): () => void {
    return this.bus.on(listener);
  }

  /**
   * Tear down the graph. Removes all listeners, clears undo history,
   * and removes DOM event bindings. Idempotent — safe to call multiple times.
   */
  override destroy(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.undoManager.clear();

    if (this._undoListener) {
      this.getDataModel().removeListener(this._undoListener);
      this.getView().removeListener(this._undoListener);
    }
    if (this._modelChangeListener) {
      this.getDataModel().removeListener(this._modelChangeListener);
    }
    if (this._selectionChangeListener) {
      this.getSelectionModel().removeListener(this._selectionChangeListener);
    }
    if (this._cellsAddedListener) {
      this.removeListener(this._cellsAddedListener);
    }
    if (this._cellsRemovedListener) {
      this.removeListener(this._cellsRemovedListener);
    }
    if (this._undoManagerUndoListener) {
      this.undoManager.removeListener(this._undoManagerUndoListener);
    }
    if (this._undoManagerRedoListener) {
      this.undoManager.removeListener(this._undoManagerRedoListener);
    }
    if (this._foldCellsListener) {
      this.removeListener(this._foldCellsListener);
    }

    this.bus.clear();
    super.destroy();
  }

  // ── Label rendering ────────────────────────────────────────────────────────

  /**
   * Cell values are stored as plain objects `{ kind, label, name, ... }`.
   * maxGraph's default converts them via toString() → "[object Object]".
   * Extract the `label` field directly.
   */
  override convertValueToString(cell: Cell): string {
    const value = cell.getValue();
    if (value !== null && typeof value === 'object') {
      // Cycle-bracket decoration cells carry `bracketLabel`, not `label`.
      // Without this they fall through to toString() → "[object Object]".
      if ((value as { kind?: unknown }).kind === 'cycle-bracket') {
        return String((value as { bracketLabel?: unknown }).bracketLabel ?? '');
      }
      if ('label' in value) {
        return String((value as { label: unknown }).label ?? '');
      }
    }
    return super.convertValueToString(cell);
  }

  // ── Domain Methods ─────────────────────────────────────────────────────────

  addMaterialNode(spec: MaterialNodeSpec): string {
    return addMaterialNode(this, spec);
  }

  updateMaterialNode(id: string, patch: Partial<MaterialNodeSpec>): void {
    updateMaterialNode(this, id, patch);
  }

  removeMaterialNode(id: string): void {
    removeMaterialNode(this, id);
  }

  addOperationNode(spec: OperationNodeSpec): string {
    return addOperationNode(this, spec);
  }

  updateOperationNode(id: string, patch: Partial<OperationNodeSpec>): void {
    updateOperationNode(this, id, patch);
  }

  removeOperationNode(id: string): void {
    removeOperationNode(this, id);
  }

  connectMaterialToOperation(materialId: string, operationId: string, storeEdgeId: string, displayName?: string, archetype?: string): void {
    this._storeEdgeToOpId.set(storeEdgeId, operationId);
    
    // For COMPLEX operations, create direct edges (no collapsing)
    if (archetype === 'COMPLEX') {
      const model = this.getDataModel();
      const materialCell = model.getCell(materialId);
      const operationCell = model.getCell(operationId);
      
      if (materialCell && operationCell) {
        this.insertEdge({
          source: materialCell,
          target: operationCell,
          id: storeEdgeId,
          style: OPERATION_EDGE_STYLE,
        });
      }
      return;
    }
    
    // For TRANSFER operations, skip edge pairing (addTransferVisualEdges handles all edges)
    if (archetype === 'TRANSFER') {
      return;
    }
    
    // Look for a matching target half in the pending queue for this operation (TRANSFORM/MEASURE)
    const pendingList = this._pendingEdgeHalves.get(operationId) ?? [];
    const targetIndex = pendingList.findIndex(p => p.side === 'target');
    
    if (targetIndex !== -1) {
      // Found a matching target half — create the visual edge and remove both halves
      const targetHalf = pendingList[targetIndex];
      pendingList.splice(targetIndex, 1);
      
      if (pendingList.length === 0) {
        this._pendingEdgeHalves.delete(operationId);
      } else {
        this._pendingEdgeHalves.set(operationId, pendingList);
      }
      
      const name = displayName || targetHalf.displayName || '';
      this._insertOperationEdge(materialId, targetHalf.materialId, operationId, name);
    } else {
      // No matching target — buffer this source half
      pendingList.push({ materialId, side: 'source', storeEdgeId, displayName });
      this._pendingEdgeHalves.set(operationId, pendingList);
    }
  }

  connectOperationToMaterial(operationId: string, materialId: string, storeEdgeId: string, displayName?: string, archetype?: string): void {
    this._storeEdgeToOpId.set(storeEdgeId, operationId);
    
    // For COMPLEX operations, create direct edges (no collapsing)
    if (archetype === 'COMPLEX') {
      const model = this.getDataModel();
      const operationCell = model.getCell(operationId);
      const materialCell = model.getCell(materialId);
      
      if (operationCell && materialCell) {
        this.insertEdge({
          source: operationCell,
          target: materialCell,
          id: storeEdgeId,
          style: OPERATION_EDGE_STYLE,
        });
      }
      return;
    }
    
    // For TRANSFER operations, skip edge pairing (addTransferVisualEdges handles all edges)
    if (archetype === 'TRANSFER') {
      return;
    }
    
    // Look for a matching source half in the pending queue for this operation (TRANSFORM/MEASURE)
    const pendingList = this._pendingEdgeHalves.get(operationId) ?? [];
    const sourceIndex = pendingList.findIndex(p => p.side === 'source');
    
    if (sourceIndex !== -1) {
      // Found a matching source half — create the visual edge and remove both halves
      const sourceHalf = pendingList[sourceIndex];
      pendingList.splice(sourceIndex, 1);
      
      if (pendingList.length === 0) {
        this._pendingEdgeHalves.delete(operationId);
      } else {
        this._pendingEdgeHalves.set(operationId, pendingList);
      }
      
      const name = displayName || sourceHalf.displayName || '';
      this._insertOperationEdge(sourceHalf.materialId, materialId, operationId, name);
    } else {
      // No matching source — buffer this target half
      pendingList.push({ materialId, side: 'target', storeEdgeId, displayName });
      this._pendingEdgeHalves.set(operationId, pendingList);
    }
  }

  removeFlowEdge(storeEdgeId: string): void {
    const operationId = this._storeEdgeToOpId.get(storeEdgeId);
    this._storeEdgeToOpId.delete(storeEdgeId);
    if (!operationId) return;

    // Remove this storeEdgeId from the pending list if it's there
    const pendingList = this._pendingEdgeHalves.get(operationId);
    if (pendingList) {
      const idx = pendingList.findIndex(p => p.storeEdgeId === storeEdgeId);
      if (idx !== -1) {
        pendingList.splice(idx, 1);
      }
      if (pendingList.length === 0) {
        this._pendingEdgeHalves.delete(operationId);
      }
    }

    // Only remove the visual edge(s) when all store halves for this operation are gone.
    const otherHalfExists = [...this._storeEdgeToOpId.values()].includes(operationId);
    if (otherHalfExists) {
      return;
    }

    const model = this.getDataModel();

    // For COMPLEX operations, remove the direct edge by storeEdgeId
    const edge = model.getCell(storeEdgeId);
    if (edge && edge.isEdge?.()) {
      this.removeCells([edge]);
    }

    // Remove primary single-edge (TRANSFORM/MEASURE)
    const primaryCell = model.getCell(`op-edge-${operationId}`);
    if (primaryCell) {
      this.removeCells([primaryCell]);
    }

    // Remove TRANSFER-specific named edges
    const transferCell = model.getCell(`op-edge-${operationId}-transfer`);
    if (transferCell) {
      this.removeCells([transferCell]);
    }
    const remainderCell = model.getCell(`op-edge-${operationId}-remainder`);
    if (remainderCell) {
      this.removeCells([remainderCell]);
    }
    const destCell = model.getCell(`op-edge-${operationId}-dest`);
    if (destCell) {
      this.removeCells([destCell]);
    }
  }

  /**
   * Move the source terminal of an operation's collapsed visual edge(s) from
   * one material to another. Used when inserting a step splices in-line: the
   * anchor's existing downstream operation must now read from the newly created
   * post-state material instead of the anchor, so the flow stays linear instead
   * of forking off the anchor.
   *
   * `storeEdgeId` is the rewired material→operation store edge; it maps to the
   * downstream operation whose visual op-edge(s) sourced from `oldSource` are
   * reconnected to `newSource`.
   */
  rewireFlowEdgeSource(
    storeEdgeId: string,
    oldSourceMaterialId: string,
    newSourceMaterialId: string,
  ): void {
    const operationId = this._storeEdgeToOpId.get(storeEdgeId);
    if (!operationId) return;

    const model = this.getDataModel();
    const oldCell = model.getCell(oldSourceMaterialId);
    const newCell = model.getCell(newSourceMaterialId);
    if (!newCell) return;

    // For COMPLEX operations, the edge is stored directly with storeEdgeId
    // For TRANSFER operations, look for named edges (remainder, transfer, dest)
    // For TRANSFORM/MEASURE, look for the op-edge
    let edgeIds: string[];
    const directEdge = model.getCell(storeEdgeId);
    if (directEdge) {
      // COMPLEX: direct edge found by storeEdgeId
      edgeIds = [storeEdgeId];
    } else {
      // TRANSFER or pairing-based edges
      edgeIds = [
        `op-edge-${operationId}`,
        `op-edge-${operationId}-transfer`,
        `op-edge-${operationId}-remainder`,
        `op-edge-${operationId}-dest`,
      ];
    }

    this.batchUpdate(() => {
      for (const id of edgeIds) {
        const edge = model.getCell(id);
        // Only reconnect the edge(s) actually sourced from the old material,
        // so a multi-input downstream op keeps its other inputs intact.
        if (edge && (!oldCell || edge.getTerminal(true) === oldCell)) {
          model.setTerminal(edge, newCell, true);
        }
      }
    });
  }

  /**
   * Create the correct visual edges for a TRANSFER operation.
   *
   * The _pendingEdgeHalves mechanism now supports multiple inputs/outputs (for COMPLEX),
   * but TRANSFER (2-in / 2-out) requires special handling to create the correct
   * three named edges (remainder, primary transfer, optional dest).
   * Call this method directly after all four store-edge connect calls have been made.
   *
   * It removes any edge that the default pairing mechanism may have
   * inserted and replaces it with up to three correct edges:
   *   1. A  → A_post  (remainder)
   *   2. A  → A+B     (primary transfer)
   *   3. B  → A+B     (dest pre-state contributes, only when dstPre is set)
   */
  addTransferVisualEdges(params: {
    operationId: string;
    srcPre: string;
    dstPre: string | null;
    srcPost: string;
    dstPost: string;
    displayName: string;
  }): void {
    const { operationId, srcPre, dstPre, srcPost, dstPost, displayName } = params;
    const model = this.getDataModel();

    // Remove any wrongly-created edge and dangling pending state
    const wrongEdge = model.getCell(`op-edge-${operationId}`);
    if (wrongEdge) {
      this.removeCells([wrongEdge]);
    }
    this._pendingEdgeHalves.delete(operationId);

    this.batchUpdate(() => {
      const srcPreCell  = model.getCell(srcPre);
      const srcPostCell = model.getCell(srcPost);
      const dstPostCell = model.getCell(dstPost);

      // 1. A → A_post (remainder)
      if (srcPreCell && srcPostCell) {
        this.insertEdge({
          id: `op-edge-${operationId}-remainder`,
          parent: this.getDefaultParent(),
          value: {
            kind: 'flowEdge' as const,
            edgeType: 'operation' as const,
            operationNodeId: operationId,
            label: 'remainder',
          },
          source: srcPreCell,
          target: srcPostCell,
          style: REMAINDER_EDGE_STYLE,
        });
      }

      // 2. A → A+B (primary transfer)
      if (srcPreCell && dstPostCell) {
        this.insertEdge({
          id: `op-edge-${operationId}-transfer`,
          parent: this.getDefaultParent(),
          value: {
            kind: 'flowEdge' as const,
            edgeType: 'operation' as const,
            operationNodeId: operationId,
            label: displayName,
          },
          source: srcPreCell,
          target: dstPostCell,
          style: OPERATION_EDGE_STYLE,
        });
      }

      // 3. B → A+B (dest pre-state)
      if (dstPre) {
        const dstPreCell = model.getCell(dstPre);
        if (dstPreCell && dstPostCell) {
          this.insertEdge({
            id: `op-edge-${operationId}-dest`,
            parent: this.getDefaultParent(),
            value: {
              kind: 'flowEdge' as const,
              edgeType: 'operation' as const,
              operationNodeId: operationId,
              label: displayName,
            },
            source: dstPreCell,
            target: dstPostCell,
            style: OPERATION_EDGE_STYLE,
          });
        }
      }
    });
  }

  private _insertOperationEdge(
    sourceMaterialId: string,
    targetMaterialId: string,
    operationId: string,
    displayName: string,
  ): void {
    const model = this.getDataModel();
    const sourceCell = model.getCell(sourceMaterialId);
    const targetCell = model.getCell(targetMaterialId);
    if (!sourceCell || !targetCell) return;

    this.batchUpdate(() => {
      this.insertEdge({
        id: `op-edge-${operationId}`,
        parent: this.getDefaultParent(),
        value: {
          kind: 'flowEdge' as const,
          edgeType: 'operation' as const,
          operationNodeId: operationId,
          label: displayName,
        },
        source: sourceCell,
        target: targetCell,
        style: OPERATION_EDGE_STYLE,
      });
    });
  }

  updateFlowEdgeLabel(operationNodeId: string, label: string): void {
    this.batchUpdate(() => {
      const model = this.getDataModel();
      const searchIn = (parent: Cell): boolean => {
        const edges = this.getChildEdges(parent);
        for (const edge of edges) {
          const val = edge.getValue();
          if (val?.operationNodeId === operationNodeId) {
            model.setValue(edge, { ...val, label });
            return true;
          }
        }
        const vertices = this.getChildVertices(parent);
        for (const v of vertices) {
          const vval = v.getValue();
          if (vval?.kind === 'group') {
            if (searchIn(v)) return true;
          }
        }
        return false;
      };
      searchIn(this.getDefaultParent());
    });
  }

  addGroup(spec: GroupSpec): string {
    return addGroup(this, spec);
  }

  updateGroup(id: string, patch: Partial<Pick<GroupSpec, 'label' | 'position' | 'size'>>): void {
    updateGroup(this, id, patch);
  }

  removeGroup(id: string, options?: { cascadeSteps?: boolean }): void {
    removeGroup(this, id, options);
  }

  addStepsToGroup(groupId: string, stepIds: string[], insertAt?: number): void {
    addStepsToGroup(this, groupId, stepIds, insertAt);
  }

  removeStepsFromGroup(groupId: string, stepIds: string[]): void {
    removeStepsFromGroup(this, groupId, stepIds);
  }

  foldGroup(groupIds: string | string[], collapse: boolean): void {
    foldGroup(this, groupIds, collapse);
  }

  hydrateFromSnapshot(snapshot: GraphSnapshot): void {
    hydrateFromSnapshot(this, snapshot);
  }

  toSnapshot(): GraphSnapshot {
    return toSnapshot(this);
  }

  clear(): void {
    clearGraph(this);
  }

  private applyDefaults(): void {
    const stylesheet = this.getStylesheet();
    stylesheet.putDefaultVertexStyle(DEFAULT_VERTEX_STYLE);
    stylesheet.putDefaultEdgeStyle(DEFAULT_EDGE_STYLE);
    // Show fold icons in the swimlane title bar.
    // collapsedImage = icon shown when the cell IS collapsed (click to expand).
    // expandedImage  = icon shown when the cell is expanded  (click to collapse).
    this.options.collapsedImage = new ImageBox('/icon/unfold-more.svg', 16, 16);
    this.options.expandedImage = new ImageBox('/icon/unfold-less.svg', 16, 16);
  }

  private configureInteraction(config: WorkflowGraphConfig): void {
    // Panning via middle-button drag only.
    // Disable usePopupTrigger so right-click goes exclusively to PopupMenuHandler.
    this.setPanning(true);
    const panningHandler = this.getPlugin<PanningHandler>('PanningHandler');
    if (panningHandler) {
      panningHandler.usePopupTrigger = false;
    }

    // Tooltips — content set by downstream code via Cell.value or overlays
    this.setTooltips(true);

    // Cells cannot be moved, resized, or inline-edited.
    // All creation and positioning happens via modals in later phases.
    this.setCellsMovable(false);
    this.setCellsResizable(false);
    this.setCellsEditable(false);
    this.setVertexLabelsMovable(false);
    this.setEdgeLabelsMovable(false);

    // Prevent unconnected (dangling) edges
    this.setAllowDanglingEdges(false);

    // Disable reconnecting by dragging edge endpoints
    this.setDisconnectOnMove(false);
    this.setCellsDisconnectable(false);

    // Use setConnectable(false) to prevent drag-to-connect via ConnectionHandler.
    // The handler remains loaded for later phases; this just disables drag creation.
    this.setConnectable(false);

    // Edges always drawn on top of vertices
    this.keepEdgesInForeground = true;

    // HTML labels ready for richer rendering in downstream phases
    this.setHtmlLabels(true);

    // Read-only mode disables all graph mutations via UI
    if (config.readOnly) {
      this.setEnabled(false);
      // maxGraph's CellRenderer only invokes the fold/unfold icon's click
      // handler when graph.isEnabled() is true (see createControlClickHandler
      // in CellRenderer). Folding is a local view state change, not a data
      // mutation, so it should stay available even when the graph is
      // otherwise disabled (e.g. the read-only run-graph-viewer sidebar).
      // forceControlClickHandler bypasses that isEnabled() check.
      this.getCellRenderer().forceControlClickHandler = true;
    }

    // Slightly larger hit area improves right-click edge targeting on thin paths.
    this.tolerance = Math.max(this.tolerance, 8);

    // Grid background
    if (config.showGrid !== false) {
      this.container.style.backgroundImage = `url(${GRID_BACKGROUND_URL})`;
      this.container.style.backgroundRepeat = 'repeat';
    }

    // Ensure the container is a proper positioned box for maxGraph's SVG.
    // Only set a default when the consumer hasn't already positioned it —
    // otherwise we'd clobber e.g. `position: absolute; inset: 0` and the div
    // would collapse to content height (canvas appears to take only half the
    // page). `absolute` is a valid containing block for the SVG.
    if (!this.container.style.position) {
      this.container.style.position = 'relative';
    }

    // Allow content to scroll when nodes extend beyond the visible area.
    // (The consumer may override via containerStyle on GraphEditorProvider.)
    if (!this.container.style.overflow) {
      this.container.style.overflow = 'auto';
    }

    // Auto-scroll the viewport when dragging near the container edge.
    // NOTE: do NOT set `this.border` here — `center()` subtracts 2*border from
    // the client size when deciding whether content overflows, so a non-zero
    // border makes it think the (non-overflowing) graph overflows horizontally
    // and skips the centering offset, leaving the graph pinned to the left.
    this.autoScroll = true;
  }

  private configureRouting(): void {
    // Straight edges are the default (edgeStyle: 'none' in DEFAULT_EDGE_STYLE).
    // Apply EdgeStyle.OrthConnector selectively on individual edges that need routing.
  }

  /**
   * Force a layout pass. Public entry point for the auto-layout toolbar button
   * and called after interactive inserts.
   */
  triggerLayout(): void {
    this._layoutManager?.executeLayout(this.getDefaultParent());
    this.refresh();
  }

  // ── Private configuration methods ─────────────────────────────────────────

  /**
   * Two coordinated hierarchical layouts (LayoutManager bubbles child→parent):
   *
   *  - GROUP layout (runs first): lays out a group's members compactly in the
   *    group's local space and resizes the swimlane to wrap them. Because the
   *    members are laid out on their own, the box is tight — it never swallows
   *    unrelated cells.
   *  - TOP layout (runs after, on the default parent): treats each group as a
   *    single node. `traverseAncestors=false` keeps it from drilling into group
   *    children, and a `getVisibleTerminal` override resolves any edge endpoint
   *    that lives inside a group to the group cell — so cross-boundary edges
   *    rank the group between its upstream and downstream neighbours (a group
   *    with no edges of its own would otherwise float off to the side).
   */
  private setupLayoutManager(): void {
    const layoutManager = new LayoutManager(this);
    this._layoutManager = layoutManager;
    layoutManager.setBubbling(true);

    const originalExecuteLayout = layoutManager.executeLayout.bind(layoutManager);
    layoutManager.executeLayout = (...args: Parameters<typeof originalExecuteLayout>) => {
      try {
        originalExecuteLayout(...args);
      } catch (error) {
        // Deletions can briefly expose dangling terminals while model edits are
        // still settling. Skip that transient pass and keep the editor stable.
        console.warn('Skipping transient layout failure', error);
      }
    };

    const configureCommon = (layout: HierarchicalLayout) => {
      layout.orientation = 'north' as HierarchicalLayout['orientation'];
      layout.intraCellSpacing = 30;
      // Even vertical rhythm between ranks for the north→south tree.
      layout.interRankCellSpacing = 90;
      // Let the layout insert POLYLINE control points so edges (curved via the
      // edge styles) bend around ranks instead of crossing straight through.
      layout.disableEdgeStyle = false;
      // Groups are ranked as single nodes / laid out on their own — never drill
      // across the group boundary in a single pass (that intermixes members
      // with outside cells and makes the group box swallow them).
      layout.traverseAncestors = false;

      // Guard hierarchical ranking from transient dangling edges (missing
      // source/target terminals), which can surface as null `temp` errors.
      const origGetEdges = layout.getEdges.bind(layout);
      layout.getEdges = (cell: Cell): Cell[] =>
        origGetEdges(cell).filter((edge) => {
          const source = layout.getVisibleTerminal(edge, true);
          const target = layout.getVisibleTerminal(edge, false);
          return source !== null && target !== null;
        });
    };

    const defaultParent = this.getDefaultParent();

    // Resolve a cell that lives inside a group to its group swimlane cell.
    const groupOf = (cell: Cell | null): Cell | null => {
      let c = cell;
      while (c && c.getParent() && c.getParent() !== defaultParent) {
        const p = c.getParent()!;
        if (p.getValue()?.kind === 'group') return p;
        c = p;
      }
      return cell;
    };

    const makeTopLayout = () => {
      const layout = new HierarchicalLayout(this);
      configureCommon(layout);

      // Resolve edge endpoints inside a group to the group cell.
      const origGetVisibleTerminal = layout.getVisibleTerminal.bind(layout);
      const resolve = (edge: Cell, source: boolean): Cell | null =>
        groupOf(origGetVisibleTerminal(edge, source));
      layout.getVisibleTerminal = resolve as typeof layout.getVisibleTerminal;

      // A group's own edge list is empty (edges attach to its member cells, not
      // the swimlane). Without this, upstream neighbours reach the group (found
      // from THEIR edge list) but the group can't reach its downstream
      // neighbours, so those detach into their own hierarchy and drift to the
      // side. Surface each member's boundary-crossing edges as the group's own.
      const origGetEdges = layout.getEdges.bind(layout);
      layout.getEdges = (cell: Cell): Cell[] => {
        if (cell.getValue()?.kind !== 'group') return origGetEdges(cell);
        const result: Cell[] = [];
        const seen = new Set<Cell>();
        for (let i = 0; i < cell.getChildCount(); i += 1) {
          const child = cell.getChildAt(i);
          for (const edge of child.getEdges(true, true)) {
            if (seen.has(edge)) continue;
            seen.add(edge);
            // Keep only edges that cross the boundary (exactly one endpoint
            // resolves to this group); internal edges map both to the group.
            if ((resolve(edge, true) === cell) !== (resolve(edge, false) === cell)) {
              result.push(edge);
            }
          }
        }
        // Keep parity with common dangling-edge guard for grouped projections.
        return result.filter((edge) => {
          const source = resolve(edge, true);
          const target = resolve(edge, false);
          return source !== null && target !== null;
        });
      };

      return layout;
    };

    const makeGroupLayout = () => {
      const layout = new HierarchicalLayout(this);
      configureCommon(layout);
      // Size the swimlane to wrap its compact internal layout, keeping its
      // top-left fixed so it doesn't jump while the top layout ranks it.
      layout.resizeParent = true;
      layout.moveParent = true;
      layout.maintainParentLocation = true;
      layout.parentBorder = 20;
      return layout;
    };

    layoutManager.getLayout = (cell: Cell | null) => {
      if (cell === this.getDefaultParent()) return makeTopLayout();
      if (cell?.getValue()?.kind === 'group') return makeGroupLayout();
      return null;
    };
  }

  private wireUndoManager(): void {
    // GraphDataModel fires CHANGE at the end of each batch update.
    // The event carries an `edit` property (UndoableEdit) which we hand
    // to UndoManager for history tracking.
    this._undoListener = (_sender: unknown, evt: EventObject) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const edit = (evt as any).getProperty('edit');
      if (edit) {
        this.undoManager.undoableEditHappened(edit);
      }
    };
    this.getDataModel().addListener(InternalEvent.CHANGE, this._undoListener);
    this.getView().addListener(InternalEvent.CHANGE, this._undoListener);
  }

  private wireEventEmitters(): void {
    // Model-changed — broad notification, emitted after any batch
    this._modelChangeListener = () => {
      this.emit({ kind: 'model-changed' });
    };
    this.getDataModel().addListener(
      InternalEvent.CHANGE,
      this._modelChangeListener
    );

    // Selection changed
    this._selectionChangeListener = () => {
      const cellIds = this.getSelectionCells()
        .map((c) => c.getId())
        .filter((id): id is string => id !== null && id !== undefined);
      this.emit({ kind: 'selection-changed', cellIds });
    };
    this.getSelectionModel().addListener(
      InternalEvent.CHANGE,
      this._selectionChangeListener
    );

    // Cells inserted
    this._cellsAddedListener = (_sender: unknown, evt: EventObject) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cells = (evt as any).getProperty('cells');
      if (Array.isArray(cells)) {
        const cellIds = cells
          .map((c) => c.getId?.())
          .filter((id): id is string => typeof id === 'string');
        this.emit({ kind: 'cells-inserted', cellIds });
      }
    };
    this.addListener(InternalEvent.CELLS_ADDED, this._cellsAddedListener);

    // Cells removed
    this._cellsRemovedListener = (_sender: unknown, evt: EventObject) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cells = (evt as any).getProperty('cells');
      if (Array.isArray(cells)) {
        const cellIds = cells
          .map((c) => c.getId?.())
          .filter((id): id is string => typeof id === 'string');
        this.emit({ kind: 'cells-removed', cellIds });
      }
    };
    this.addListener(InternalEvent.CELLS_REMOVED, this._cellsRemovedListener);

    // Undo / Redo emitted from UndoManager events
    this._undoManagerUndoListener = () => {
      this.emit({ kind: 'undo' });
    };
    this.undoManager.addListener(
      InternalEvent.UNDO,
      this._undoManagerUndoListener
    );

    this._undoManagerRedoListener = () => {
      this.emit({ kind: 'redo' });
    };
    this.undoManager.addListener(
      InternalEvent.REDO,
      this._undoManagerRedoListener
    );

    // Fold / unfold (swimlane collapse button)
    this._foldCellsListener = (_sender: unknown, evt: EventObject) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cells: Cell[] = (evt as any).getProperty('cells') ?? [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const collapse: boolean = (evt as any).getProperty('collapse') ?? false;
      const foldedGroups = cells
        .map((c) => ({ id: c.getId(), val: c.getValue() }))
        .filter(({ id, val }) => id && val?.kind === 'group')
        .map(({ id }) => ({ id: id!, isCollapsed: collapse }));
      if (foldedGroups.length > 0) {
        this.emit({ kind: 'cells-folded', foldedGroups });
      }
    };
    this.addListener(InternalEvent.FOLD_CELLS, this._foldCellsListener);
  }

  private emit(event: WorkflowGraphEvent): void {
    if (this.disposed) return;
    this.bus.emit(event);
  }
}
